import { XMarkIcon } from "@heroicons/react/20/solid";
import { ModalConfig, useModalStore } from "@/stores/use-modal-store";

type Availability = "Video" | "Scene";

interface QualityScoreDefinition {
  label: string;
  color: string;
  availability: Availability[];
  description: string;
  outcome: string;
}

interface PillarDefinition {
  label: string;
  description: string;
  qualityScores: QualityScoreDefinition[];
}

// Swatch colours match the quality score lines in VideoScenePerformanceGraph.
const PILLAR_DEFINITIONS: PillarDefinition[] = [
  {
    label: "Attention",
    description: "The viewer's attentional experience is efficient, coherent, and sustained.",
    qualityScores: [
      { label: "Hook", color: "#A7F3D0", availability: ["Video", "Scene"], description: "Securing viewer attention in the opening moments.", outcome: "VTR" },
      { label: "Visual ease", color: "#38BDF8", availability: ["Video", "Scene"], description: "Smooth, coherent guidance of attention through the video.", outcome: "Drop off" },
      { label: "Hold", color: "#10B981", availability: ["Video", "Scene"], description: "Sustaining viewer attention across the full duration.", outcome: "Drop off" },
    ],
  },
  {
    label: "Emotion",
    description: "The viewer experiences genuine, well-structured affective engagement.",
    qualityScores: [
      { label: "Intensity", color: "#F2318A", availability: ["Video", "Scene"], description: "Emotional intensity - meaningful affective engagement", outcome: "Brand building" },
      { label: "Payoff", color: "#F2318A", availability: ["Video"], description: "Coherent emotional arc - setup finds payoff.", outcome: "Brand building" },
      { label: "Authenticity", color: "#F2318A", availability: ["Video"], description: "Emotion feels genuine, not manufactured.", outcome: "Brand building" },
    ],
  },
  {
    label: "Memory",
    description: "The brand is encoded durably and linked to the right mental associations.",
    qualityScores: [
      {
        label: "Brand attribution",
        color: "#8B5CF6",
        availability: ["Video", "Scene"],
        description: "Distinctive brand assets recognizable and encoded.",
        outcome: "Brand building",
      },
      { label: "Message memory", color: "#8B5CF6", availability: ["Video"], description: "The key message / meaning is remembered.", outcome: "Message memory" },
    ],
  },
];

const CHIP_CLASS_NAME = "rounded-lg border border-[#9ca3af] font-semibold whitespace-nowrap";

// The video in-depth view is always rendered on a fixed dark background regardless of the app
// theme, so this modal matches that instead of the theme-aware daisyUI modal-box.
export const VIDEO_METRIC_DEFINITIONS_MODAL_CONFIG: ModalConfig = { className: "w-full max-w-[890px]" };

/**
 * VideoMetricDefinitionsModal component
 *
 * Reference table defining Creative Impact, the Attention / Emotion / Memory pillars and their quality
 * scores, whether each is available per video or per scene, and the business outcome it predicts.
 */
export const VideoMetricDefinitionsModal = () => {
  /* Hooks */
  const { close } = useModalStore((state) => state.modalActions);

  /* Render */
  return (
    <div className="flex flex-col gap-4 -m-6 p-4 rounded-2xl bg-[#1d232a] text-white">
      {/* Header */}
      <div className="flex items-center gap-2.5 px-3">
        <h2 className="flex-1 font-semibold text-lg">Definitions</h2>
        <button type="button" onClick={close} aria-label="Close" className="flex items-center justify-center size-9 cursor-pointer">
          <XMarkIcon className="size-4" />
        </button>
      </div>

      <div className="flex flex-col rounded-lg overflow-hidden">
        {/* Column headers */}
        <div className="flex items-center gap-3 h-11 px-4 bg-[#323c49] text-sm font-semibold">
          <span className="w-44 shrink-0">Metric</span>
          <span className="w-30 shrink-0">Available</span>
          <span className="flex-1">Description</span>
          <span className="w-[147px] shrink-0">Outcomes</span>
        </div>

        <div className="flex items-center gap-3 p-4 border-b border-[#323c49]">
          <span className="w-44 shrink-0 font-semibold">Creative Impact</span>
          <div className="w-30 shrink-0">
            <span className={`${CHIP_CLASS_NAME} px-1.5 py-0.5 text-xs`}>Video</span>
          </div>
          <p className="flex-1 text-sm text-white/70">The overall effectiveness of the video. Top level score made up of Attention, Emotion and Memory.</p>
          <div className="w-[147px] shrink-0">
            <span className={`${CHIP_CLASS_NAME} inline-flex px-2 py-1 text-sm`}>Brand &amp; sales</span>
          </div>
        </div>

        {PILLAR_DEFINITIONS.map((pillar) => (
          <div key={pillar.label} className="flex flex-col">
            <div className="flex items-center gap-3 p-4 bg-[#242c35] border-b border-[#323c49]">
              <span className="w-44 shrink-0 font-semibold">{pillar.label}</span>
              <div className="w-30 shrink-0">
                <span className={`${CHIP_CLASS_NAME} px-1.5 py-0.5 text-xs`}>Video</span>
              </div>
              <p className="flex-1 text-sm text-white/70">{pillar.description}</p>
            </div>

            {pillar.qualityScores.map((qualityScore) => (
              <div key={qualityScore.label} className="flex items-center gap-3 min-h-11 px-4 py-3 border-b border-[#323c49]">
                <div className="flex items-center gap-3 w-44 shrink-0">
                  <span className="size-3 rounded-xs shrink-0 m-0.5" style={{ backgroundColor: qualityScore.color }} />
                  <span className="text-sm font-semibold">{qualityScore.label}</span>
                </div>
                <div className="flex items-center gap-1.5 w-30 shrink-0">
                  {qualityScore.availability.map((level) => (
                    <span key={level} className={`${CHIP_CLASS_NAME} px-1.5 py-0.5 text-xs`}>
                      {level}
                    </span>
                  ))}
                </div>
                <p className="flex-1 text-sm text-white/70">{qualityScore.description}</p>
                <div className="w-[147px] shrink-0">
                  <span className={`${CHIP_CLASS_NAME} inline-flex px-2 py-1 text-sm`}>{qualityScore.outcome}</span>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
